import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Briefcase, GraduationCap, Award, Code, Globe, Heart, FileText, Sparkles } from 'lucide-react';

export default function CVBuilder() {
  const { token } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showTailoredModal, setShowTailoredModal] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedDocs, setGeneratedDocs] = useState(null);
  const [cv, setCv] = useState({
    personal_info: { linkedin: '' },
    work_experience: [{ job_title: '', company_name: '', start_date: '', end_date: '', description: '' }],
    education: [{ institution_name: '', qualification: '', start_date: '', end_date: '', notes: '' }],
    certifications: [{ certificate_title: '', issuing_body: '', issue_date: '', expiry_date: '', certificate_id: '' }],
    skills: [{ skill_name: '', level: '' }],
    languages: [{ language: '', proficiency: '' }],
    hobbies: [{ hobby_name: '' }],
    projects: [{ project_title: '', description: '', technologies_used: '', project_link: '' }]
  });

  useEffect(() => {
    fetchCV();
  }, []);

  const fetchCV = async () => {
    try {
      const response = await axios.get(`${API}/cv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.user_id) {
        setCv(response.data);
      }
    } catch (error) {
      console.log('No existing CV');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/cv`, cv, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('CV saved successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save CV');
    } finally {
      setLoading(false);
    }
  };

  const addItem = (section) => {
    const templates = {
      work_experience: { job_title: '', company_name: '', start_date: '', end_date: '', description: '' },
      education: { institution_name: '', qualification: '', start_date: '', end_date: '', notes: '' },
      certifications: { certificate_title: '', issuing_body: '', issue_date: '', expiry_date: '', certificate_id: '' },
      skills: { skill_name: '', level: '' },
      languages: { language: '', proficiency: '' },
      hobbies: { hobby_name: '' },
      projects: { project_title: '', description: '', technologies_used: '', project_link: '' }
    };
    setCv(prev => ({ ...prev, [section]: [...prev[section], templates[section]] }));
  };

  const removeItem = (section, index) => {
    setCv(prev => ({ ...prev, [section]: prev[section].filter((_, i) => i !== index) }));
  };

  const updateItem = (section, index, field, value) => {
    setCv(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const handleGenerateTailoredCV = async () => {
    if (!jobDescription.trim()) {
      toast.error('Please enter a job description');
      return;
    }
    if (!jobTitle.trim()) {
      toast.error('Please enter job title');
      return;
    }
    if (!companyName.trim()) {
      toast.error('Please enter company name');
      return;
    }

    setGenerating(true);
    setGeneratedDocs(null);
    
    try {
      const response = await axios.post(
        `${API}/cv-simple/generate`,
        { 
          job_description: jobDescription,
          job_title: jobTitle,
          company_name: companyName
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // The endpoint now returns both CV and Cover Letter
      if (response.data && response.data.cv) {
        // Download CV
        const cvBlob = base64ToBlob(response.data.cv.data, 'application/pdf');
        const cvUrl = URL.createObjectURL(cvBlob);
        downloadFile(cvUrl, response.data.cv.filename);

        // Download Cover Letter
        const clBlob = base64ToBlob(response.data.cover_letter.data, 'application/pdf');
        const clUrl = URL.createObjectURL(clBlob);
        downloadFile(clUrl, response.data.cover_letter.filename);

        setGeneratedDocs(response.data);
        toast.success(`✅ CV and Cover Letter generated! 5 credits deducted. Credits remaining: ${response.data.credits_remaining}`);
      }
    } catch (error) {
      console.error('Generation Error:', error);
      
      // Handle insufficient credits error
      if (error.response?.status === 402) {
        toast.error(
          error.response?.data?.detail || 'Insufficient credits',
          {
            duration: 5000,
            action: {
              label: 'Get Credits',
              onClick: () => navigate('/credits')
            }
          }
        );
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate documents');
      }
    } finally {
      setGenerating(false);
    }
  };

  const base64ToBlob = (base64, contentType) => {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  };

  const downloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCloseModal = () => {
    setShowTailoredModal(false);
    setJobDescription('');
    setJobTitle('');
    setCompanyName('');
    setGeneratedDocs(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      {/* AI CV Generator Modal */}
      {showTailoredModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold flex items-center gap-2 dark:text-gray-200">
                  <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  Generate CV & Cover Letter
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  disabled={generating}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  Our AI will create a perfectly tailored, ATS-friendly CV and a professional Cover Letter optimized for your target job!
                </p>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-amber-900 font-medium">
                    Cost: <strong>5 Credits</strong> (for both CV + Cover Letter)
                  </p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <Label htmlFor="jobTitle" className="mb-2 block font-semibold">
                    Job Title *
                  </Label>
                  <Input
                    id="jobTitle"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g., Senior Software Engineer"
                    className="border-2 focus:border-purple-500"
                    disabled={generating}
                  />
                </div>

                <div>
                  <Label htmlFor="companyName" className="mb-2 block font-semibold">
                    Company Name *
                  </Label>
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g., Google, Microsoft, etc."
                    className="border-2 focus:border-purple-500"
                    disabled={generating}
                  />
                </div>

                <div>
                  <Label htmlFor="jobDescription" className="mb-2 block font-semibold">
                    Job Description *
                  </Label>
                  <Textarea
                    id="jobDescription"
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the complete job description here (responsibilities, requirements, qualifications, etc.)..."
                    rows={10}
                    className="w-full resize-none border-2 focus:border-purple-500"
                    disabled={generating}
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Tip: Include the full job posting for best results
                  </p>
                </div>
              </div>

              {/* Success State with Download Buttons */}
              {generatedDocs && !generating && (
                <div className="mb-4 p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-green-900 font-semibold">CV and Cover Letter Generated Successfully!</h3>
                      <p className="text-green-700 text-sm">Your tailored documents have been downloaded. Click below to download again.</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        const blob = base64ToBlob(generatedDocs.cv.data, 'application/pdf');
                        const url = URL.createObjectURL(blob);
                        downloadFile(url, generatedDocs.cv.filename);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download CV
                    </button>
                    <button
                      onClick={() => {
                        const blob = base64ToBlob(generatedDocs.cover_letter.data, 'application/pdf');
                        const url = URL.createObjectURL(blob);
                        downloadFile(url, generatedDocs.cover_letter.filename);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Cover Letter
                    </button>
                    <Button
                      variant="outline"
                      onClick={handleCloseModal}
                      className="border-green-600 text-green-700 hover:bg-green-50"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={handleCloseModal}
                  disabled={generating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateTailoredCV}
                  disabled={generating || !jobDescription.trim() || !jobTitle.trim() || !companyName.trim()}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  {generating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating Your CV (may take 1-2 minutes)...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate CV
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-2">CV Builder</h1>
            <p className="text-gray-600">Build your professional CV with structured information</p>
            
            {/* Generate Tailored CV Button */}
            <div className="mt-4 flex gap-3 flex-wrap">
              <Button
                onClick={() => setShowTailoredModal(true)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate ATS-Friendly CV for Job
              </Button>
              
              <Button
                onClick={() => navigate('/cv-history')}
                variant="outline"
                className="border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View History
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Personal Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="linkedin">LinkedIn Profile URL (Optional)</Label>
                    <Input
                      id="linkedin"
                      type="url"
                      placeholder="https://linkedin.com/in/your-profile"
                      value={cv.personal_info?.linkedin || ''}
                      onChange={(e) => setCv(prev => ({ 
                        ...prev, 
                        personal_info: { ...prev.personal_info, linkedin: e.target.value } 
                      }))}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Other contact details (name, email, phone) will be automatically pulled from your account and Personal Details section.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Work Experience */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Work Experience
                  </CardTitle>
                  <Button size="sm" onClick={() => addItem('work_experience')}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {cv.work_experience.map((item, idx) => (
                  <div key={idx} className="p-4 border rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold">Experience #{idx + 1}</h4>
                      {cv.work_experience.length > 1 && (
                        <Button size="sm" variant="destructive" onClick={() => removeItem('work_experience', idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label>Job Title *</Label>
                        <Input value={item.job_title} onChange={(e) => updateItem('work_experience', idx, 'job_title', e.target.value)} required />
                      </div>
                      <div>
                        <Label>Company *</Label>
                        <Input value={item.company_name} onChange={(e) => updateItem('work_experience', idx, 'company_name', e.target.value)} required />
                      </div>
                      <div>
                        <Label>Start Date *</Label>
                        <Input type="month" value={item.start_date} onChange={(e) => updateItem('work_experience', idx, 'start_date', e.target.value)} required />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input type="month" value={item.end_date} onChange={(e) => updateItem('work_experience', idx, 'end_date', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <Label>Description *</Label>
                      <Textarea value={item.description} onChange={(e) => updateItem('work_experience', idx, 'description', e.target.value)} rows={3} required />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Education */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    Education
                  </CardTitle>
                  <Button size="sm" onClick={() => addItem('education')}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {cv.education.map((item, idx) => (
                  <div key={idx} className="p-4 border rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold">Education #{idx + 1}</h4>
                      {cv.education.length > 1 && (
                        <Button size="sm" variant="destructive" onClick={() => removeItem('education', idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label>Institution *</Label>
                        <Input value={item.institution_name} onChange={(e) => updateItem('education', idx, 'institution_name', e.target.value)} required />
                      </div>
                      <div>
                        <Label>Qualification *</Label>
                        <Input value={item.qualification} onChange={(e) => updateItem('education', idx, 'qualification', e.target.value)} required />
                      </div>
                      <div>
                        <Label>Start Date *</Label>
                        <Input type="month" value={item.start_date} onChange={(e) => updateItem('education', idx, 'start_date', e.target.value)} required />
                      </div>
                      <div>
                        <Label>End Date *</Label>
                        <Input type="month" value={item.end_date} onChange={(e) => updateItem('education', idx, 'end_date', e.target.value)} required />
                      </div>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea value={item.notes} onChange={(e) => updateItem('education', idx, 'notes', e.target.value)} rows={2} />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Certifications */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Certifications
                  </CardTitle>
                  <Button size="sm" onClick={() => addItem('certifications')}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {cv.certifications.map((item, idx) => (
                  <div key={idx} className="p-4 border rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold">Certificate #{idx + 1}</h4>
                      <Button size="sm" variant="destructive" onClick={() => removeItem('certifications', idx)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label>Certificate Title *</Label>
                        <Input value={item.certificate_title} onChange={(e) => updateItem('certifications', idx, 'certificate_title', e.target.value)} required />
                      </div>
                      <div>
                        <Label>Issuing Body *</Label>
                        <Input value={item.issuing_body} onChange={(e) => updateItem('certifications', idx, 'issuing_body', e.target.value)} required />
                      </div>
                      <div>
                        <Label>Issue Date *</Label>
                        <Input type="date" value={item.issue_date} onChange={(e) => updateItem('certifications', idx, 'issue_date', e.target.value)} required />
                      </div>
                      <div>
                        <Label>Expiry Date</Label>
                        <Input type="date" value={item.expiry_date} onChange={(e) => updateItem('certifications', idx, 'expiry_date', e.target.value)} />
                      </div>
                      <div>
                        <Label>Certificate ID</Label>
                        <Input value={item.certificate_id} onChange={(e) => updateItem('certifications', idx, 'certificate_id', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Skills */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Code className="w-5 h-5" />
                    Skills
                  </CardTitle>
                  <Button size="sm" onClick={() => addItem('skills')}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {cv.skills.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input placeholder="Skill name" value={item.skill_name} onChange={(e) => updateItem('skills', idx, 'skill_name', e.target.value)} />
                      <select className="px-3 py-2 border rounded-lg" value={item.level} onChange={(e) => updateItem('skills', idx, 'level', e.target.value)}>
                        <option value="">Level</option>
                        <option value="Beginner">Beginner</option>
                        <option value="Intermediate">Intermediate</option>
                        <option value="Advanced">Advanced</option>
                        <option value="Expert">Expert</option>
                      </select>
                      <Button size="sm" variant="destructive" onClick={() => removeItem('skills', idx)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Languages */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Languages
                  </CardTitle>
                  <Button size="sm" onClick={() => addItem('languages')}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {cv.languages.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input placeholder="Language" value={item.language} onChange={(e) => updateItem('languages', idx, 'language', e.target.value)} />
                      <select className="px-3 py-2 border rounded-lg" value={item.proficiency} onChange={(e) => updateItem('languages', idx, 'proficiency', e.target.value)}>
                        <option value="">Proficiency</option>
                        <option value="Basic">Basic</option>
                        <option value="Conversational">Conversational</option>
                        <option value="Fluent">Fluent</option>
                        <option value="Native">Native</option>
                      </select>
                      <Button size="sm" variant="destructive" onClick={() => removeItem('languages', idx)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Projects */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Projects</CardTitle>
                  <Button size="sm" onClick={() => addItem('projects')}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {cv.projects.map((item, idx) => (
                  <div key={idx} className="p-4 border rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold">Project #{idx + 1}</h4>
                      <Button size="sm" variant="destructive" onClick={() => removeItem('projects', idx)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label>Project Title *</Label>
                        <Input value={item.project_title} onChange={(e) => updateItem('projects', idx, 'project_title', e.target.value)} required />
                      </div>
                      <div>
                        <Label>Description *</Label>
                        <Textarea value={item.description} onChange={(e) => updateItem('projects', idx, 'description', e.target.value)} rows={3} required />
                      </div>
                      <div>
                        <Label>Technologies Used</Label>
                        <Input placeholder="e.g., React, Node.js, MongoDB" value={item.technologies_used} onChange={(e) => updateItem('projects', idx, 'technologies_used', e.target.value)} />
                      </div>
                      <div>
                        <Label>Project Link</Label>
                        <Input type="url" placeholder="https://" value={item.project_link} onChange={(e) => updateItem('projects', idx, 'project_link', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Hobbies */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    Hobbies & Interests
                  </CardTitle>
                  <Button size="sm" onClick={() => addItem('hobbies')}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-3">
                  {cv.hobbies.map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input placeholder="Hobby" value={item.hobby_name} onChange={(e) => updateItem('hobbies', idx, 'hobby_name', e.target.value)} />
                      <Button size="sm" variant="destructive" onClick={() => removeItem('hobbies', idx)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-center pb-8">
              <Button onClick={handleSave} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 px-8 py-6 text-lg">
                {loading ? 'Saving...' : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save CV
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
